/* quick-view.js — product card "Quick view" → modal with a compact buy box fetched via
   the Section Rendering API. Reuses product.js (variant logic) + cart-drawer.js (add).
   Framework-free, deferred. */
(function () {
  'use strict';

  function a11y(key, fallback) {
    return window.puString ? window.puString(key, fallback) : fallback;
  }
  function esc(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  var modal, panel, opener, hidden;
  // Same set the cart and menu drawers use. Radios are filtered below: only the
  // checked one in a group is a tab stop, so an unchecked chip cannot be the
  // trap's end and let Tab leave the dialog.
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function ensureModal() {
    if (modal) return;
    modal = document.createElement('div');
    modal.className = 'quick-view-modal';
    modal.innerHTML =
      '<div class="quick-view-modal__overlay" data-qv-close></div>' +
      '<div class="quick-view-modal__panel" role="dialog" aria-modal="true" aria-label="' + esc(a11y('quickView', 'Quick view')) + '">' +
      '<button type="button" class="quick-view-modal__close" data-qv-close aria-label="' + esc(a11y('closeQuickView', 'Close quick view')) + '">' +
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg></button>' +
      '<div data-qv-content></div></div>';
    document.body.appendChild(modal);
    panel = modal.querySelector('[data-qv-content]');
    modal.addEventListener('click', function (e) { if (e.target.closest('[data-qv-close]')) close(); });
    document.addEventListener('keydown', onKeydown, true);
    // Add to cart: close without focusing the card trigger, so #CartDrawer
    // can take focus directly. Esc, the chip, and the backdrop still return it.
    document.addEventListener('submit', function (e) {
      if (e.target.closest('#QuickViewForm')) close({ restoreFocus: false });
    });
    // The head add listener stops the submit before it bubbles. cart-drawer.js
    // dispatches this instead so the cart can take focus.
    document.addEventListener('cart:add-form', function (e) {
      var form = e.detail && e.detail.form;
      if (form && form.getAttribute('id') === 'QuickViewForm') close({ restoreFocus: false });
    });

    // Mini gallery: clicking a thumb swaps the main image. Delegated on the panel (which
    // persists across opens; only its innerHTML changes), so no listeners stack up.
    panel.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-qv-thumb]');
      if (thumb) activateThumb(thumb);
    });
    // Jump the gallery to a variant's image when the buy box reports a variant change
    // (product.js dispatches this on the document with the variant's media id).
    document.addEventListener('product:variant-media', function (e) {
      var id = e.detail && e.detail.mediaId;
      if (id == null || !modal.classList.contains('is-open')) return;
      var thumb = panel.querySelector('[data-qv-thumb][data-media-id="' + id + '"]');
      if (thumb) activateThumb(thumb);
    });
  }

  function activateThumb(thumb) {
    var main = panel.querySelector('[data-qv-main]');
    if (main && thumb.dataset.src) main.src = thumb.dataset.src;
    panel.querySelectorAll('[data-qv-thumb]').forEach(function (t) {
      t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
    });
  }

  function isShown(el) {
    return !el.closest('[hidden]') && el.getClientRects().length > 0;
  }

  // Tab stops inside the dialog, in DOM order. Unchecked radios are omitted.
  function tabStops(dialog) {
    var nodes = dialog.querySelectorAll(FOCUSABLE);
    var stops = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.tabIndex < 0 || !isShown(el)) continue;
      if (el.matches('input[type="radio"]') && !isTabbableRadio(el, dialog)) continue;
      stops.push(el);
    }
    return stops;
  }

  function isTabbableRadio(el, dialog) {
    var name = el.name;
    if (!name || typeof CSS === 'undefined' || !CSS.escape) return el.checked;
    var group = dialog.querySelectorAll('input[type="radio"][name="' + CSS.escape(name) + '"]');
    var chosen = null;
    var i;
    for (i = 0; i < group.length; i++) {
      if (group[i].checked && isShown(group[i])) { chosen = group[i]; break; }
    }
    if (!chosen) {
      for (i = 0; i < group.length; i++) {
        if (isShown(group[i])) { chosen = group[i]; break; }
      }
    }
    return el === chosen;
  }

  function focusClose() {
    var btn = modal.querySelector('.quick-view-modal__close');
    if (!btn) return;
    // A plain focus() from the opening click does not match :focus-visible, so
    // the close chip's ring never paints. focusVisible forces that ring.
    var opts = { preventScroll: true, focusVisible: true };
    btn.focus(opts);
    if (document.activeElement === btn) {
      hideBackground();
      return;
    }
    requestAnimationFrame(function () {
      if (!modal.classList.contains('is-open')) return;
      btn.focus(opts);
      if (document.activeElement === btn) hideBackground();
    });
  }

  // Hide the rest of the page only after focus is inside the dialog. Chrome
  // blocks aria-hidden on an ancestor that still contains focus.
  function hideBackground() {
    if (hidden || !modal) return;
    hidden = [];
    var nodes = [];
    var main = document.getElementById('MainContent');
    if (main) nodes.push(main);
    var kids = document.body.children;
    var i;
    for (i = 0; i < kids.length; i++) nodes.push(kids[i]);
    for (i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!el || el.nodeType !== 1 || el === modal) continue;
      if (modal.contains(el) || el.contains(modal)) continue;
      var tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') continue;
      var seen = false;
      var s;
      for (s = 0; s < hidden.length; s++) {
        if (hidden[s].el === el) { seen = true; break; }
      }
      if (seen) continue;
      if (document.activeElement && el.contains(document.activeElement)) continue;
      var hadAria = el.hasAttribute('aria-hidden');
      var ariaVal = hadAria ? el.getAttribute('aria-hidden') : null;
      var hadInert = el.hasAttribute('inert');
      if (hadAria && ariaVal === 'true' && hadInert) continue;
      hidden.push({ el: el, aria: hadAria, ariaVal: ariaVal, inert: hadInert });
      if (!(hadAria && ariaVal === 'true')) el.setAttribute('aria-hidden', 'true');
      if (!hadInert) el.setAttribute('inert', '');
    }
  }

  function showBackground() {
    if (!hidden) return;
    var list = hidden;
    hidden = null;
    for (var i = 0; i < list.length; i++) {
      var rec = list[i];
      var el = rec.el;
      if (!el) continue;
      if (rec.aria) el.setAttribute('aria-hidden', rec.ariaVal == null ? '' : rec.ariaVal);
      else el.removeAttribute('aria-hidden');
      if (!rec.inert) el.removeAttribute('inert');
    }
  }

  function onKeydown(e) {
    if (!modal || !modal.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var dialog = modal.querySelector('.quick-view-modal__panel');
    if (!dialog) return;
    var stops = tabStops(dialog);
    if (!stops.length) {
      e.preventDefault();
      return;
    }
    var first = stops[0];
    var last = stops[stops.length - 1];
    var active = document.activeElement;
    var inside = dialog.contains(active);
    if (e.shiftKey) {
      if (!inside || active === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      }
    } else if (!inside || active === last) {
      e.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  function open(handle, trigger) {
    ensureModal();
    opener = (trigger && typeof trigger.focus === 'function') ? trigger : document.activeElement;
    panel.innerHTML = '<p class="quick-view-modal__loading">' + esc(a11y('loading', 'Loading…')) + '</p>';
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    focusClose();
    fetch('/products/' + handle + '?section_id=quick-view')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (!modal.classList.contains('is-open')) return;
        panel.innerHTML = html;
      })
      .catch(function () { window.location.href = '/products/' + handle; });
  }

  function close(opts) {
    if (!modal || !modal.classList.contains('is-open')) return;
    var restore = !opts || opts.restoreFocus !== false;
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    // The cart drawer shares body overflow. Closing this modal clears it.
    // Put the drawer's lock back, including the mobile position and top pin,
    // when that sheet is still open.
    var cartDrawer = document.querySelector('cart-drawer.is-open');
    if (cartDrawer && typeof cartDrawer.reassertPageLock === 'function') cartDrawer.reassertPageLock();
    // Lift aria-hidden and inert before focus moves. Otherwise Chrome warns
    // "Blocked aria-hidden on an element because its descendant retained focus"
    // when the trigger inside main#MainContent is focused.
    showBackground();
    var back = opener;
    opener = null;
    // Escape runs this from keydown, so the trigger's :focus-visible ring returns.
    // Add to cart passes restoreFocus:false so the cart drawer takes focus.
    if (restore && back && typeof back.focus === 'function' && document.contains(back)) {
      back.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    open(btn.getAttribute('data-quick-view'), btn);
  });
})();
